<template>
    <div class="adaptation-block">
        <main class="adaptation-block__column my-home-page home-accesses">
            <MyHomePageHeader :title="$t('myHome.accessesTitle')" @back="$router.push({ name: 'my-home' })" />
            <p v-if="loading" class="home-accesses__status" role="status">{{ $t('myHome.loading') }}</p>
            <div v-else-if="error" class="home-accesses__status" role="alert">
                <p>{{ $t(error) }}</p>
                <button type="button" class="home-accesses__retry" @click="retryKey++">{{ $t('myHome.retry') }}</button>
            </div>
            <p v-else-if="!home?.has_access" class="home-accesses__status">{{ $t('myHome.noAccess') }}</p>
            <template v-else>
                <div v-if="logoUrl && !logoFailed" class="home-accesses__logo home-accesses__corners">
                    <img :src="logoUrl" :alt="home.client.complex_name || ''" @error="logoFailed = true">
                </div>
                <dl class="home-accesses__address">
                    <dt>{{ $t('myHome.building') }}:</dt><dd>{{ home.client.complex_name || home.client.building_name || '—' }}</dd>
                    <dt>{{ $t('myHome.address') }}:</dt><dd>{{ home.client.address || '—' }}</dd>
                </dl>
                <ul v-if="entryPoints.length" class="home-accesses__list" :aria-label="$t('myHome.accessesTitle')">
                    <li v-for="point in entryPoints" :key="point.id" class="home-accesses__entry">
                        <div class="home-accesses__type">
                            <img v-if="types[point.type]" :src="types[point.type].icon" :alt="$t(types[point.type].label)">
                        </div>
                        <div class="home-accesses__name home-accesses__corners">{{ point.title }}</div>
                        <div class="home-accesses__identification">
                            <img v-if="point.identification_types.includes('car_license_plate')" :src="icons.plate" :alt="$t('myHome.plateIdentification')" class="home-accesses__plate">
                            <span v-if="point.identification_types.includes('cards')" class="home-accesses__cards" role="img" :aria-label="$t('myHome.cardsIdentification')">
                                <img :src="icons.card" alt=""><img :src="icons.chip" alt="">
                            </span>
                            <img v-if="point.identification_types.includes('face_identification')" :src="icons.face" :alt="$t('myHome.faceIdentification')">
                        </div>
                    </li>
                </ul>
                <p v-else class="home-accesses__status" role="status">{{ $t('myHome.noEntrances') }}</p>
            </template>
        </main>
    </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useMainStore } from '@/store/mainStore'
import ApiManager from '@/services/ApiManager'
import { getHomeAuthToken } from '@/services/myHome'
import MyHomePageHeader from '@/components/MyHomePageHeader.vue'
import gate from '@/assets/images/accesses/gate.svg'
import video from '@/assets/images/accesses/video_intercom.svg'
import intercom from '@/assets/images/accesses/chip_intercom.svg'
import face from '@/assets/images/accesses/face.svg'
import card from '@/assets/images/accesses/card.svg'
import chip from '@/assets/images/accesses/chip.svg'
import plate from '@/assets/images/accesses/plate.svg'

export default {
    name: 'MyHomeAccessesView',
    components: { MyHomePageHeader },
    setup() {
        const store = useMainStore()
        const home = ref(null)
        const loading = ref(true)
        const error = ref(null)
        const retryKey = ref(0)
        const logoFailed = ref(false)
        const types = {
            car_gate: { icon: gate, label: 'myHome.carGate' },
            video_intercom: { icon: video, label: 'myHome.videoIntercom' },
            intercom_without_video: { icon: intercom, label: 'myHome.chipIntercom' }
        }
        const entryPoints = computed(() => [...new Map((home.value?.entry_points || []).map(point => [point.id, point])).values()])
        const logoUrl = computed(() => {
            try {
                const url = new URL(home.value?.client?.complex_logo_url)
                return ['https:', 'http:'].includes(url.protocol) ? url.href : null
            } catch { return null }
        })
        watch(() => [store.login, retryKey.value], async ([login], previous, onCleanup) => {
            home.value = null
            error.value = null
            logoFailed.value = false
            loading.value = true
            let active = true
            const controller = new AbortController()
            onCleanup(() => { active = false; controller.abort() })
            if (!login) {
                loading.value = false
                error.value = 'myHome.noAccess'
                return
            }
            try {
                const token = await getHomeAuthToken(login)
                if (!active) return
                const response = await ApiManager.getMyHomeDetails(token, controller.signal)
                if (active) home.value = response.data
            } catch (requestError) {
                if (active) {
                    const status = requestError.response?.status
                    error.value = status === 409 ? 'myHome.ambiguous' :
                        [401, 403, 404].includes(status) ? 'myHome.noAccess' : 'myHome.unavailable'
                }
            } finally {
                if (active) loading.value = false
            }
        }, { immediate: true, flush: 'sync' })
        return { home, loading, error, retryKey, logoFailed, logoUrl, entryPoints, types, icons: { face, card, chip, plate } }
    }
}
</script>

<style scoped>
.home-accesses__corners { position: relative; background: linear-gradient(#cacaca,#cacaca) left top/8px 1px no-repeat, linear-gradient(#cacaca,#cacaca) right top/8px 1px no-repeat, linear-gradient(#cacaca,#cacaca) left bottom/8px 1px no-repeat, linear-gradient(#cacaca,#cacaca) right bottom/8px 1px no-repeat, linear-gradient(#cacaca,#cacaca) left top/1px 8px no-repeat, linear-gradient(#cacaca,#cacaca) right top/1px 8px no-repeat, linear-gradient(#cacaca,#cacaca) left bottom/1px 8px no-repeat, linear-gradient(#cacaca,#cacaca) right bottom/1px 8px no-repeat; }
.home-accesses__corners::before { content: ''; position: absolute; inset: 3px; background: rgb(202 202 202 / .1); pointer-events: none; }
.home-accesses__logo { width: 194px; height: 92px; margin: 10px auto 22px; display: grid; place-items: center; }
.home-accesses__logo img { position: relative; width: 55px; height: 64px; object-fit: contain; }
.home-accesses__address { display: grid; grid-template-columns: 73px minmax(0, 1fr); gap: 8px; margin: 0 3px 26px; color: var(--home-muted); font-size: 11px; line-height: 12px; }
.home-accesses__address dt { font-family: 'Montserrat', sans-serif; font-weight: 500; }
.home-accesses__address dd { margin: 0; overflow-wrap: anywhere; }
.home-accesses__list { list-style: none; margin: 0 1px; padding: 26px 0 0; border-top: 1px solid #34343b; display: flex; flex-direction: column; gap: 32px; }
.home-accesses__entry { display: grid; grid-template-columns: 27px minmax(0, 1fr) 124px; align-items: center; gap: 10px; }
.home-accesses__type { display: flex; align-items: center; justify-content: center; }
.home-accesses__type img { max-width: 27px; max-height: 34px; }
.home-accesses__name { min-height: 38px; padding: 6px 10px; display: flex; align-items: center; font-family: 'Montserrat', sans-serif; font-weight: 500; font-size: 10px; line-height: 12px; overflow-wrap: anywhere; }
.home-accesses__identification { display: flex; justify-content: flex-end; align-items: center; gap: 6px; }
.home-accesses__identification > img { width: 18px; height: 18px; object-fit: contain; }
.home-accesses__identification > .home-accesses__plate { width: 48px; height: 15px; }
.home-accesses__cards { display: flex; align-items: center; gap: 6px; }
.home-accesses__cards img:first-child { width: 22px; height: 15px; }
.home-accesses__cards img:last-child { width: 18px; height: 18px; }
.home-accesses__status { margin: 40px 0; font-family: 'Montserrat', sans-serif; font-size: 13px; line-height: 20px; text-align: center; }
.home-accesses__retry { margin-top: 18px; padding: 0; border: 0; background: transparent; color: var(--home-green); font: inherit; cursor: pointer; }
@media (max-width: 360px) { .home-accesses__entry { grid-template-columns: 27px minmax(0, 1fr); } .home-accesses__identification { grid-column: 2; justify-content: flex-start; } }
</style>
